/**
 * The working-hours editor — FR-A8 §4, interface I-1.
 *
 * Seven rows, Monday first: a switch for open or closed and, when open, two
 * 24-hour times. *Copy Monday to weekdays* fills Tuesday to Friday, and a free
 * note carries what a grid cannot (*"By appointment on Saturdays"*).
 *
 * `null` is **not given**, which is not *closed all week*: a firm that has not
 * said is drawn as an empty state with one button, and only that button makes
 * a week. Onboarding's *Address & hours* step (12B) reuses this component.
 */

import { Button, Input, Switch } from '@fiveox/ui'
import { Clock, Copy } from 'lucide-react'

import type { WorkingHours } from '../../../lib/api/firm'
import {
  DEFAULT_DAY,
  WEEKDAYS,
  WEEKDAY_LABELS,
  copyMondayToWeekdays,
  hoursSummary,
  startingHours,
} from './rules'

export function WorkingHoursEditor({
  value,
  onChange,
  disabled,
}: {
  value: WorkingHours | null
  onChange: (next: WorkingHours | null) => void
  disabled?: boolean
}) {
  if (!value) {
    return (
      <div className="border-border flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-8 text-center">
        <span className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full">
          <Clock size={18} aria-hidden />
        </span>
        <div>
          <p className="text-foreground font-medium">No working hours yet</p>
          <p className="text-muted-foreground mt-1 max-w-sm text-sm">
            Your website’s footer can show when the office is open. Start from a Monday-to-Friday
            week and change what differs.
          </p>
        </div>
        {!disabled && (
          <Button variant="outline" onClick={() => onChange(startingHours())}>
            Add working hours
          </Button>
        )}
      </div>
    )
  }

  const setDay = (day: (typeof WEEKDAYS)[number], slot: { open: string; close: string } | null) =>
    onChange({ ...value, days: { ...value.days, [day]: slot } })

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm" data-hours-summary>
        {hoursSummary(value)}
      </p>
      <ul className="divide-border border-border divide-y rounded-xl border">
        {WEEKDAYS.map((day) => {
          const slot = value.days[day] ?? null
          return (
            <li
              key={day}
              data-day={day}
              className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-4"
            >
              <div className="flex items-center justify-between gap-3 sm:w-48">
                <span className="text-foreground text-sm font-medium">{WEEKDAY_LABELS[day]}</span>
                <Switch
                  checked={Boolean(slot)}
                  disabled={disabled}
                  srLabel={`${WEEKDAY_LABELS[day]} open`}
                  onChange={(e) => setDay(day, e.target.checked ? { ...DEFAULT_DAY } : null)}
                />
              </div>
              {slot ? (
                <div className="grid flex-1 grid-cols-2 gap-3">
                  <Input
                    type="time"
                    aria-label={`${WEEKDAY_LABELS[day]} opens`}
                    value={slot.open}
                    disabled={disabled}
                    onChange={(e) => setDay(day, { ...slot, open: e.target.value })}
                  />
                  <Input
                    type="time"
                    aria-label={`${WEEKDAY_LABELS[day]} closes`}
                    value={slot.close}
                    disabled={disabled}
                    onChange={(e) => setDay(day, { ...slot, close: e.target.value })}
                  />
                </div>
              ) : (
                <p className="text-muted-foreground flex-1 text-sm">Closed</p>
              )}
            </li>
          )
        })}
      </ul>
      <Input
        label="A note for visitors"
        placeholder="By appointment on Saturdays"
        maxLength={200}
        value={value.note ?? ''}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, note: e.target.value || null })}
      />
      {!disabled && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => onChange(copyMondayToWeekdays(value))}>
            <Copy size={14} aria-hidden /> Copy Monday to weekdays
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
            Remove working hours
          </Button>
        </div>
      )}
    </div>
  )
}
